/**
 * Adapters mapping heritage-app-backend REST responses to the UI-facing types
 * the check-in app already uses. Keeping the UI types stable is what lets the
 * GraphQL→REST migration happen with no component changes (no regression).
 */
import { UserProfile, UserTypes } from '@/shared/types/auth'
import { BranchRecord, MeResponse, ParentRecord } from './types'

/**
 * Derive the legacy `userTypes` enum array from the REST `/auth/me` response.
 * The old GraphQL backend computed these server-side; the REST API instead
 * exposes `permissions` + `childrenChurchRoles`, from which we reconstruct the
 * same flags the RBAC UI (PermissionGate / useUserTypes) depends on.
 */
export function deriveUserTypes(
    me: Pick<MeResponse, 'permissions' | 'childrenChurchRoles'>
): UserTypes[] {
    const permissions = me.permissions ?? []
    const roles = me.childrenChurchRoles ?? []
    const hasRole = (name: string) => roles.some((r) => r.role === name)
    const hasPerm = (name: string) => permissions.includes(name)

    const types: UserTypes[] = []

    if (hasRole('parent')) types.push(UserTypes.IS_PARENT)
    if (hasRole('teacher')) types.push(UserTypes.IS_TEACHER)
    if (hasRole('pending_teacher')) types.push(UserTypes.IS_PENDING_TEACHER)
    if (hasRole('admin_teacher') || hasPerm('admin.teacher')) {
        types.push(UserTypes.IS_ADMIN_TEACHER)
    }
    if (hasPerm('sudo')) types.push(UserTypes.IS_GLOBAL_ADMIN)

    if (types.length === 0) types.push(UserTypes.OTHERS)
    return Array.from(new Set(types))
}

/** True when the user holds any teacher-flavoured children-church role. */
export function isTeacherUser(me: MeResponse): boolean {
    return (me.childrenChurchRoles ?? []).some((r) =>
        ['teacher', 'admin_teacher', 'pending_teacher', 'suspended_teacher'].includes(
            r.role
        )
    )
}

/**
 * Build the UI `UserProfile` from `/auth/me`, optionally enriched with the
 * parent record (for `parent._id` / branch) and the branch list (for branch
 * name resolution). `parent` and `branches` are optional so callers can fetch
 * them only when the user is a parent.
 */
export function meToUserProfile(
    me: MeResponse,
    parent?: ParentRecord | null,
    branches?: BranchRecord[] | null
): UserProfile {
    const branchId = parent?.branchId ?? undefined
    const branchName = branchId
        ? branches?.find((b) => b._id === branchId)?.name
        : undefined

    const parentBlock =
        me.parentId || parent
            ? {
                  _id: parent?._id ?? me.parentId ?? '',
                  phoneNumber: parent?.phoneNumber ?? me.phoneNumber ?? '',
                  relationship: parent?.relationship ?? '',
                  branch: {
                      _id: branchId ?? '',
                      name: branchName ?? ''
                  }
              }
            : undefined

    return {
        _id: me.id ?? '',
        firstName: me.firstName ?? '',
        lastName: me.lastName ?? '',
        auth0UserId: me.auth0UserId,
        email: me.email ?? '',
        isTeacher: isTeacherUser(me),
        picture: me.picture ?? undefined,
        imageUrl: me.imageUrl ?? me.picture ?? undefined,
        parent: parentBlock,
        roles: (me.roles ?? []).map((r) => ({
            _id: r.id,
            language: 'en',
            name: r.name,
            permissions: (me.permissions ?? []).map((p) => ({ _id: p, name: p }))
        })),
        permissions: me.permissions ?? [],
        userTypes: deriveUserTypes(me)
    }
}

/** Permission strings that grant access to the content-management CMS. */
export const CONTENT_MANAGE_PERMISSION = 'children-church_content_manager'
export const ADMIN_TEACHER_PERMISSION = 'admin.teacher'
export const SUDO_PERMISSION = 'sudo'

/**
 * True when the user may manage Anointed Kids content — content managers,
 * admin teachers, and super admins (sudo). Mirrors the backend admin guards.
 */
export function canManageContent(permissions?: string[]): boolean {
    if (!permissions) return false
    return (
        permissions.includes(CONTENT_MANAGE_PERMISSION) ||
        permissions.includes(ADMIN_TEACHER_PERMISSION) ||
        permissions.includes(SUDO_PERMISSION)
    )
}
